import { useCallback, useEffect, useRef, useState } from 'react';
import { getDetectionProfile, updateDetectionProfile } from '../../../services/detectionProfile';
import { describeHttpError } from '../../../utils/apiError';
import { describeConflict, versionConflictMessage } from '../../../utils/conflictMessages';
import { buildMasterPatch } from '../../../constants/analyticsConstants';
import { useAutoDismiss } from '../../../hooks/useAutoDismiss';

/**
 * Профиль детекции камеры (Part 4, разделы 4 и 6.2–6.3; ТЗ задачи 21, 22, 26).
 *
 * Извлечено из AnalyticsTab, чтобы Task 14 (Admin → Edit Camera) переиспользовал
 * тот же сетевой слой вместо второго стора того же состояния.
 *
 * Профиль детекции — authoritative состояние бэкенда. Хук не считает
 * effective_enabled, не решает, какой детектор нужен метрике, и не показывает
 * успех до ответа: состояние перерисовывается только тем профилем, который
 * вернул сервер.
 *
 * PATCH отправляет ПОЛНЫЙ набор manual_features, явный disable_features и
 * expected_version. Разбор 409 — единой точкой conflictMessages.describeConflict
 * (context 'detection_profile'), чтобы словарь кодов не дублировался между
 * хуком и analyticsConstants (Task 8 review): dependency
 * (detector_required_by_active_rules) — не ошибка, а вопрос пользователю;
 * version (в т.ч. detection_profile_version_conflict) и unknown (например,
 * legacy_people_runtime_locked) — перечитывание профиля, без оптимистичного
 * отката локального состояния (его тут и не было — чекбокс всегда рисуется
 * профилем с сервера).
 *
 * Master-переключатель «Все детекции» (ТЗ 24, 25) не хранит отдельный boolean:
 * setMaster вычисляет патч из текущего профиля (deriveMasterState/
 * buildMasterPatch) и идёт тем же sendPatch, что и toggleManual. При 409
 * dependency featureId приходит пустой строкой — это признак master-флоу,
 * и pendingForce.featureId в этом случае помечается '__master__'.
 */

const buildManualFeatures = (profile, featureId, manual) => {
  const current = profile.features
    .filter((feature) => feature.manual_enabled)
    .map((feature) => feature.feature_id)
    .filter((id) => id !== featureId);

  return manual ? [...current, featureId] : current;
};

export const useDetectionProfile = (cameraId, { enabled = true, onRulesChanged } = {}) => {
  const [profile, setProfile] = useState(null);
  const [phase, setPhase] = useState('idle'); // idle | loading | ready | error
  const [errorMessage, setErrorMessage] = useState('');

  const [busyFeatureId, setBusyFeatureId] = useState('');
  const [actionError, setActionError] = useState('');
  const [notice, setNotice] = useState('');
  const [pendingForce, setPendingForce] = useState(null);

  // FE-FIX08: notice — результат успешного применения (что деактивировалось),
  // гаснет сам; actionError/errorMessage остаются до действия пользователя.
  useAutoDismiss(notice, setNotice);

  // Ref, а не состояние: нужен только чтобы loadProfile знал, есть ли уже
  // отрисованный профиль, не завися от него как от deps useCallback — иначе
  // identity loadProfile менялась бы при каждом setProfile и переигрывала
  // бы монтирующий useEffect по кругу.
  const profileRef = useRef(profile);
  useEffect(() => { profileRef.current = profile; }, [profile]);

  /**
   * silent=true — фоновое перечитывание (после force, version-конфликта или
   * legacy_people_runtime_locked): карточка «Детекции» с чекбоксами и текстом
   * ошибки/уведомления не должна пропадать под скелетон на время рефетча,
   * пока на экране уже есть профиль. Явный вызов (первая загрузка, кнопка
   * «Повторить» через `load`) по-прежнему показывает скелетон.
   */
  const loadProfile = useCallback(({ silent = false } = {}) => {
    if (!cameraId || !enabled) {
      setProfile(null);
      setPhase('idle');
      return Promise.resolve();
    }

    if (!silent || !profileRef.current) setPhase('loading');

    // actionError здесь НЕ чистится: перечитывание профиля после 409-конфликта
    // как раз и сопровождается сообщением о чужих изменениях.
    return getDetectionProfile(cameraId)
      .then((data) => {
        setProfile(data);
        setPhase('ready');
      })
      .catch((error) => {
        // Ошибка молчаливого рефетча — тоже ошибка: тихим остаётся только
        // отсутствие лишнего скелетона, а не сам факт сбоя загрузки.
        setProfile(null);
        setErrorMessage(describeHttpError(error, 'Не удалось загрузить настройки детекций'));
        setPhase('error');
      });
  }, [cameraId, enabled]);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  // Смена камеры обнуляет всё локальное состояние: чужие правила и версия
  // к новой камере отношения не имеют.
  useEffect(() => {
    setNotice('');
    setPendingForce(null);
    setActionError('');
  }, [cameraId]);

  /**
   * Ответ PATCH — сам профиль (production не отдаёт convenience-поле
   * deactivated_rules). После force-отключения состояние зависимых правил
   * знает только backend, поэтому список правил перечитывается запросом.
   */
  const applyResult = useCallback((result, { force = false } = {}) => {
    const updatedProfile = result?.profile ?? result;
    if (updatedProfile) setProfile(updatedProfile);

    const deactivated = Array.isArray(result?.deactivated_rules) ? result.deactivated_rules : [];

    if (force) {
      setNotice(deactivated.length > 0
        ? `Детекция выключена. Деактивированы правила: ${deactivated.map((rule) => rule.name || rule.rule_id).join(', ')}`
        : 'Детекция выключена. Состояние зависимых правил обновлено.');
      if (onRulesChanged) onRulesChanged();
      // Ответ PATCH применяется сразу (выше), но после force единственный
      // authoritative источник состояния зависимых правил — backend, поэтому
      // профиль ещё и перечитывается отдельным запросом. silent: true — карточка
      // с уже применённым результатом и уведомлением не должна прятаться под
      // скелетон на время этого рефетча.
      loadProfile({ silent: true });
      return;
    }

    // ТЗ №33: после любого успешного изменения профиль перечитывается —
    // состояние тумблеров рисуется только тем, что вернул backend.
    loadProfile({ silent: true });

    if (deactivated.length === 0) {
      setNotice('');
      return;
    }

    setNotice(`Деактивированы правила: ${deactivated.map((rule) => rule.name || rule.rule_id).join(', ')}`);
    if (onRulesChanged) onRulesChanged();
  }, [onRulesChanged, loadProfile]);

  /**
   * Выключение детекции передаётся отдельным полем disable_features: пустой
   * manual_features сам по себе не выключает фичу, которую держит активное
   * правило, — backend обязан узнать намерение явно.
   */
  const sendPatch = useCallback((manualFeatures, { force, disableFeatures = [], featureId = '' }) => {
    if (!profile) return Promise.resolve();

    return updateDetectionProfile(cameraId, {
      expected_version: profile.version,
      manual_features: manualFeatures,
      disable_features: disableFeatures,
      force,
    })
      .then((result) => {
        applyResult(result, { force });
        setPendingForce(null);
        return true;
      })
      .catch((error) => {
        // ТЗ №33: 412 (устаревшая expected_version) — тот же конфликт версии,
        // что и 409: сообщение и перечитывание профиля.
        if (error?.response?.status === 412) {
          setActionError(versionConflictMessage('detection_profile'));
          setPendingForce(null);
          return loadProfile({ silent: true }).then(() => false);
        }

        const conflict = describeConflict(error, 'detection_profile');

        if (conflict.kind === 'dependency') {
          // Фоллбэк на featureId: если backend не назвал заблокированную фичу
          // явно, целимся в ту, что как раз переключали, а не в [undefined].
          const blockedFeatureId = conflict.detail.features?.[0] ?? conflict.detail.feature_id ?? featureId;

          setPendingForce({
            // Пустой featureId — признак master-флоу (у него нет одной
            // «заблокированной» фичи), поэтому pendingForce.featureId в этом
            // случае помечается '__master__', а не именем фичи.
            featureId: featureId || '__master__',
            dependentRules: conflict.detail.dependent_rules,
            manualFeatures,
            disableFeatures: featureId ? [blockedFeatureId] : disableFeatures,
            error: '',
          });
          return false;
        }

        if (conflict.kind === 'unknown' || conflict.kind === 'version') {
          // ТЗ 22/25: заранее известный отказ backend'а (legacy_people_runtime_locked)
          // и конфликт версии обрабатываются одинаково — сообщение пользователю
          // и перечитывание профиля, без оптимистичного отката чекбокса
          // (единственный источник правды — то, что вернул backend).
          setActionError(conflict.message);
          setPendingForce(null);
          // silent: true — на экране уже есть профиль, карточка с текстом
          // ошибки не должна на время рефетча подменяться скелетоном.
          return loadProfile({ silent: true }).then(() => false);
        }

        const message = describeHttpError(error, 'Не удалось изменить настройки детекций');
        if (pendingForce) setPendingForce((prev) => (prev ? { ...prev, error: message } : prev));
        else setActionError(message);
        return false;
      });
  }, [applyResult, cameraId, loadProfile, pendingForce, profile]);

  const toggleManual = useCallback((feature, manual) => {
    setActionError('');
    setNotice('');
    setBusyFeatureId(feature.feature_id);

    return sendPatch(buildManualFeatures(profile, feature.feature_id, manual), {
      force: false,
      disableFeatures: manual ? [] : [feature.feature_id],
      featureId: feature.feature_id,
    }).finally(() => setBusyFeatureId(''));
  }, [profile, sendPatch]);

  /**
   * ТЗ 24, 25: master-переключатель «Все детекции» не хранит собственного
   * состояния — патч считается из профиля прямо перед отправкой, тем же
   * sendPatch, что и toggleManual (единая точка обработки 409/force/refetch).
   */
  const setMaster = useCallback((on) => {
    if (!profile) return Promise.resolve();
    setActionError('');
    setNotice('');
    const patch = buildMasterPatch(profile, on);
    setBusyFeatureId('__master__');

    return sendPatch(patch.manual_features, {
      force: false,
      disableFeatures: patch.disable_features,
      featureId: '',
    }).finally(() => setBusyFeatureId(''));
  }, [profile, sendPatch]);

  const confirmForce = useCallback(() => {
    if (!pendingForce) return Promise.resolve();
    setBusyFeatureId(pendingForce.featureId);

    return sendPatch(pendingForce.manualFeatures, {
      force: true,
      disableFeatures: pendingForce.disableFeatures || [pendingForce.featureId],
      featureId: pendingForce.featureId,
    }).finally(() => setBusyFeatureId(''));
  }, [pendingForce, sendPatch]);

  const cancelForce = useCallback(() => setPendingForce(null), []);

  return {
    profile,
    phase,
    errorMessage,
    actionError,
    notice,
    busyFeatureId,
    pendingForce,
    load: loadProfile,
    toggleManual,
    setMaster,
    confirmForce,
    cancelForce,
  };
};

export default useDetectionProfile;
